import {useTranslation} from 'react-i18next';

import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {Box, Button, InputAdornment, MenuItem, Select, TextField, Typography, useTheme} from '@mui/material';

import {getFieldStyle} from '../../../../../layouts/theme/FieldStyles';
import type {ProfileComparisonFilterState} from '../../utils/ProfileComparison/filterProfileComparisonProfiles';

export interface Props {
  filters: ProfileComparisonFilterState;
  seniorityOptions: string[];
  departmentName: string;
  profileCount: number;
  hasActiveFilters: boolean;
  onFiltersChange: (filters: ProfileComparisonFilterState) => void;
  onClear: () => void;
}

export const ProfileComparisonSearchBar = ({
  filters,
  seniorityOptions,
  departmentName,
  profileCount,
  hasActiveFilters,
  onFiltersChange,
  onClear,
}: Props) => {
  const theme = useTheme();
  const fieldSx = getFieldStyle(theme);
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDepartmentHealthPage.profileComparison.search'});
  const {t: tSeniority} = useTranslation();

  const resultsHint = hasActiveFilters
    ? t('resultsFiltered', {count: profileCount})
    : t('resultsAll', {count: profileCount, department: departmentName});

  return (
    <Box sx={{mb: 1.5}}>
      <Box sx={{display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap'}}>
        <TextField
          size='small'
          value={filters.search}
          onChange={(event) => onFiltersChange({...filters, search: event.target.value})}
          placeholder={String(t('placeholder'))}
          sx={{...fieldSx, flex: 1, minWidth: 180, '& .MuiOutlinedInput-input': {py: 1.125, pl: 0.5}}}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{fontSize: 15, color: 'text.secondary'}} />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size='small'
          value={filters.seniority ?? ''}
          onChange={(event) =>
            onFiltersChange({
              ...filters,
              seniority: event.target.value ? String(event.target.value) : null,
            })
          }
          displayEmpty
          sx={{...fieldSx, minWidth: 130}}
        >
          <MenuItem value=''>{t('allSeniority')}</MenuItem>
          {seniorityOptions.map((seniority) => (
            <MenuItem key={seniority} value={seniority}>
              {tSeniority(`seniorityLevels.${seniority}`, {defaultValue: seniority})}
            </MenuItem>
          ))}
        </Select>
        <Select
          size='small'
          value={filters.alignmentTier}
          onChange={(event) =>
            onFiltersChange({
              ...filters,
              alignmentTier: event.target.value as ProfileComparisonFilterState['alignmentTier'],
            })
          }
          sx={{...fieldSx, minWidth: 140}}
        >
          <MenuItem value='all'>{t('allTiers')}</MenuItem>
          <MenuItem value='strong'>{t('tierStrong')}</MenuItem>
          <MenuItem value='review'>{t('tierReview')}</MenuItem>
          <MenuItem value='at_risk'>{t('tierAtRisk')}</MenuItem>
        </Select>
        <Button
          size='small'
          variant='outlined'
          startIcon={<ClearIcon sx={{fontSize: 15}} />}
          onClick={onClear}
          disabled={!hasActiveFilters}
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            py: 0.5,
            px: 1.25,
            borderColor: 'divider',
            color: 'text.secondary',
            '&:hover': {bgcolor: 'action.hover', borderColor: 'text.disabled'},
          }}
        >
          {t('clear')}
        </Button>
      </Box>
      <Typography sx={{mt: 1.25, fontSize: '12px', color: 'text.secondary'}}>{resultsHint}</Typography>
    </Box>
  );
};
